import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { obtenerMovimiento, TIPOS } from './api/movimientosApi.js';
import CapturaDeMovimiento from './pantallas/CapturaDeMovimiento.jsx';
import CapturaDeTraspaso from './pantallas/CapturaDeTraspaso.jsx';
import DetalleDeMovimiento from './pantallas/DetalleDeMovimiento.jsx';
import ListaDeMovimientos from './pantallas/ListaDeMovimientos.jsx';
import AvisoDeError from '../../nucleo/avisos/AvisoDeError.jsx';

/**
 * Destino "Movimientos" del incremento 3/6 (RF-059).
 *
 * El módulo se enchufa al shell con la sección que el núcleo ya registra —clave
 * `movimientos`, ruta `/movimientos`, etiqueta corta "Movs", orden 2— y aquí
 * resuelve sus propias rutas internas. NO reclama el sexto lugar, que es de
 * Ajustes (RN-044b), y NO modifica el shell.
 *
 * LA RUTA `captura-rapida` SIGUE RESPONDIENDO Y SIGUE ABRIENDO EL GASTO (RN-410,
 * RN-430), y no se da de baja: quien la tenga guardada como acceso directo en el
 * teléfono NO pierde su toque único.
 * [CORREGIDO 2026-10-03 · RN-450] Esta nota decía que es «la ruta a la que apunta
 * el botón flotante del shell». Ya no: desde el incremento 09 el botón flotante
 * ABRE EL MENÚ DE TIPOS (RF-150, D-244) y ninguna de sus cuatro opciones usa esta
 * ruta. Lo que cambió es qué hace el BOTÓN, no qué hace la DIRECCIÓN.
 */
export default function Movimientos() {
  return (
    <Routes>
      <Route index element={<ListaDeMovimientos />} />

      {/* Las tres capturas de una sola cuenta comparten pantalla y cambian de cara
          según el tipo: son cinco campos y ninguno más (RF-048). */}
      <Route path="captura-rapida" element={<CapturaDeMovimiento tipo={TIPOS.gasto} />} />
      <Route path="gasto" element={<CapturaDeMovimiento tipo={TIPOS.gasto} />} />
      <Route path="ingreso" element={<CapturaDeMovimiento tipo={TIPOS.ingreso} />} />
      <Route path="devolucion" element={<CapturaDeMovimiento tipo={TIPOS.devolucion} />} />

      {/* Traspaso, pago de tarjeta y disposición de efectivo: UNA sola pantalla que
          cambia de nombre según la clase de las dos cuentas (D-38, D-46). */}
      <Route path="traspaso" element={<CapturaDeTraspaso />} />

      <Route path=":idMovimiento" element={<DetalleDeMovimiento />} />
      <Route path=":idMovimiento/editar" element={<EdicionSegunSuTipo />} />

      <Route path="*" element={<Navigate to="/movimientos" replace />} />
    </Routes>
  );
}

/**
 * La edición reutiliza la pantalla de captura DEL TIPO QUE YA TIENE el movimiento.
 *
 * EL TIPO NO SE OFRECE PARA CAMBIARLO (D-35, RN-073): un gasto no se convierte en
 * traspaso; se cancela y se captura de nuevo. La pantalla lo toma del propio
 * movimiento —por eso lo consulta antes de decidir qué formulario abrir— y el
 * servidor rechaza cualquier intento de cambiarlo con
 * TIPO_MOVIMIENTO_NO_MODIFICABLE aunque la petición llegue directa a la API.
 */
function EdicionSegunSuTipo() {
  const { idMovimiento } = useParams();
  const [tipo, setTipo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    obtenerMovimiento(idMovimiento)
      .then((detalle) => { if (vigente) setTipo(detalle.movimiento.tipo); })
      .catch((fallo) => { if (vigente) setError(fallo); });
    return () => { vigente = false; };
  }, [idMovimiento]);

  if (error) return <AvisoDeError error={error} />;
  if (!tipo) return <div className="aviso i">Abriendo el movimiento…</div>;

  return tipo === TIPOS.traspaso
    ? <CapturaDeTraspaso />
    : <CapturaDeMovimiento tipo={tipo} />;
}
